import React, { useMemo, useState } from 'react';
import { Map, MapPin, Users } from 'lucide-react';
import Badge from '../common/Badge';
import DataTable from '../DataTable';
import { useAppContext } from '../../context/AppContext';
import { t } from '../../i18n';

const stakeholderSeed = [
  { id: 'st-1', tipo: 'Municipalidad', actor: 'Ilustre Municipalidad Maria Elena', estado: 'Coordinacion activa', riesgo: 'Bajo' },
  { id: 'st-2', tipo: 'Comunidad', actor: 'Junta de Vecinos Valle Norte', estado: 'Reunion mensual', riesgo: 'Medio' },
  { id: 'st-3', tipo: 'Autoridad', actor: 'SEREMI Medio Ambiente', estado: 'Observaciones abiertas', riesgo: 'Alto' }
];

const buildMapSrc = (coordinates) => {
  if (!coordinates) {
    return 'https://www.openstreetmap.org/export/embed.html?bbox=-83%2C-56%2C-66%2C-17&layer=mapnik';
  }
  const lat = Number(coordinates.lat || 0);
  const lng = Number(coordinates.lng || 0);
  const delta = 2.2;
  const left = lng - delta;
  const right = lng + delta;
  const bottom = lat - delta;
  const top = lat + delta;
  return `https://www.openstreetmap.org/export/embed.html?bbox=${left}%2C${bottom}%2C${right}%2C${top}&layer=mapnik&marker=${lat}%2C${lng}`;
};

const riskTone = {
  Alto: 'danger',
  Medio: 'warning',
  Bajo: 'success'
};

const stakeholderColumns = [
  { key: 'tipo', label: 'Tipo', sortable: true },
  {
    key: 'actor',
    label: 'Actor',
    sortable: true,
    render: (value) => <span className="font-semibold text-slate-900">{t(value)}</span>
  },
  { key: 'estado', label: 'Estado', sortable: true },
  {
    key: 'riesgo',
    label: 'Riesgo social',
    sortable: true,
    render: (value) => <Badge tone={riskTone[value] || 'neutral'}>{t(value)}</Badge>
  }
];

const DevelopmentGeoReference = () => {
  const { renewablePortfolio } = useAppContext();
  const [selectedProjectId, setSelectedProjectId] = useState(renewablePortfolio[0]?.id || '');

  const selectedProject = useMemo(
    () => renewablePortfolio.find((item) => item.id === selectedProjectId) || renewablePortfolio[0] || null,
    [renewablePortfolio, selectedProjectId]
  );

  const mapSrc = buildMapSrc(selectedProject?.coordinates);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">{t('Referencia Geografica')}</h1>
        <p className="text-sm text-slate-500">{t('Coordinacion territorial, comunidades y trazado geografico del proyecto.')}</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-slate-700">
            <Map className="w-4 h-4 text-neutral-400" />
            <span className="font-semibold">{t('Mapa de referencia territorial')}</span>
          </div>
          <select
            value={selectedProjectId}
            onChange={(event) => setSelectedProjectId(event.target.value)}
            className="px-3 py-2 rounded-lg border border-slate-200 text-sm"
          >
            {renewablePortfolio.map((project) => (
              <option key={project.id} value={project.id}>{t(project.name)}</option>
            ))}
          </select>
        </div>

        <div className="rounded-xl overflow-hidden border border-slate-200 h-80">
          <iframe title={t('Geo referencia proyecto')} className="w-full h-full" src={mapSrc} />
        </div>

        <div className="mt-3 text-sm text-slate-600 flex flex-wrap gap-4">
          <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4 text-neutral-400" />{t(selectedProject?.region)} / {t(selectedProject?.commune)}</span>
          <span>{t(selectedProject?.technology)}· {t(selectedProject?.mw)} {t('MW')}</span>
          <span>{t('Responsable:')} {t(selectedProject?.responsibleName)}</span>
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-3 text-slate-700">
          <Users className="w-4 h-4 text-neutral-400" />
          <span className="font-semibold">{t('Coordinacion con desarrollo territorial y comunidades')}</span>
        </div>
        <DataTable
          data={stakeholderSeed}
          columns={stakeholderColumns}
          searchPlaceholder={t('Buscar actor...')}
          emptyTitle={t('Sin actores registrados')}
          emptyDescription={t('No hay coordinacion territorial registrada para este proyecto.')}
        />
      </div>
    </div>
  );
};

export default DevelopmentGeoReference;
